/**
 * Dashboard widget: the member's tiered profile-completion score as a circular
 * arc (replaces the old horizontal bar) + a CTA to the profile page. Renders
 * nothing on error (non-critical).
 *
 * Powered by GET /api/profile/score. See
 * docs/investigations/onboarding-incentive-redesign-prep-2026-05-11.md.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { fetchProfileScore } from '../lib/api';

const RADIUS = 34;
const CIRC = 2 * Math.PI * RADIUS;

export default function ProfileScoreCard() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['profile', 'score'],
    queryFn: fetchProfileScore,
    staleTime: 60_000,
  });

  if (isError) return null; // Non-critical widget — fail silently.

  if (isLoading || !data) {
    return (
      <div className="bg-white rounded-3xl shadow-sm p-5 flex items-center gap-5 animate-pulse">
        <div className="w-20 h-20 rounded-full bg-gray-100 shrink-0" />
        <div className="flex-1">
          <div className="h-4 w-36 bg-gray-200 rounded mb-2" />
          <div className="h-3 w-44 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  const pct = Math.round(data.score * 100);
  const isComplete = data.score >= 1.0;
  const remaining = Math.max(0, data.denominator - data.filled);
  const ring =
    data.score >= 0.9 ? '#10B981' : data.score >= 0.65 ? '#F59E0B' : '#5B4FE8';
  const dash = CIRC * Math.min(1, Math.max(0, data.score));

  return (
    <div className="bg-white rounded-3xl shadow-sm p-4 flex items-center gap-5">
      {/* Circular arc — replaces the stacked horizontal bar to vary the rhythm. */}
      <div className="relative shrink-0 w-20 h-20">
        <svg viewBox="0 0 80 80" className="w-20 h-20 -rotate-90">
          <circle cx="40" cy="40" r={RADIUS} fill="none" stroke="#F1F5F9" strokeWidth="7" />
          <circle
            cx="40" cy="40" r={RADIUS} fill="none"
            stroke={ring} strokeWidth="7" strokeLinecap="round"
            strokeDasharray={`${dash} ${CIRC}`}
            className="transition-[stroke-dasharray] duration-700 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold font-display text-navy tabular-nums leading-none">{pct}%</span>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <h3 className="text-base font-bold font-display text-[#111827]">Complete your profile</h3>
        {isComplete ? (
          <>
            <p className="text-xs text-gray-500 mt-0.5">All {data.denominator} answers in — nicely done.</p>
            <p className="text-sm text-green-600 font-medium inline-flex items-center gap-1.5 mt-2">
              <CheckCircle2 className="w-4 h-4" /> Profile complete
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 mt-0.5">
              <span className="font-semibold text-[#111827] tabular-nums">{remaining}</span> question{remaining === 1 ? '' : 's'} remaining for better survey matches
            </p>
            <Link
              to="/profile"
              className="group inline-flex items-center gap-1 text-sm text-primary font-semibold mt-2"
            >
              Update profile
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
